import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";
import { debounce } from "lodash";

import {
  FileTextOutlined,
  FilePdfOutlined,
  RedoOutlined,
  Loading3QuartersOutlined,
  ArrowUpOutlined,
  OrderedListOutlined,
  ExclamationCircleOutlined,
  CrownFilled,
} from "@ant-design/icons";

import {
  theme,
  Card,
  Button,
  message,
  Table,
  Tag,
  Tooltip,
  Space,
  Typography,
  Flex,
} from "antd";
const { Text } = Typography;

import CONSTANTS from "shared/constants";
import ApiManager from "resources/ApiManager/api";
import strings from "resources/strings";
import { getFormattedUnixDate } from "shared/utilities";
import ModalUnsupportedPlan from "../../../components/Shared/ModalUnsupportedPlan";
import {
  CfdiTags,
  CfdiAmount,
  CfdiFileButton,
  EfosTag,
} from "../../../components/Shared/CfdiElements";

export default function TableCFDIs({ cfdiType, activeTab, efosRfc }) {
  const { token: theme_token } = theme.useToken();
  const { isSmallView, isMediumView } = useWindowDimensions();
  const navigate = useNavigate();

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);
  const rfcRecentCfdis = useStore((state) => state.rfcRecentCfdis);
  const setRfcRecentCfdis = useStore((state) => state.setRfcRecentCfdis);
  const cfdiSearchFilters = useStore((state) => state.cfdiSearchFilters);

  const active_rfc_obj = user.rfcs[activeRfc];

  const [tableData, setTableData] = useState(
    efosRfc
      ? null
      : activeRfc in rfcRecentCfdis
        ? rfcRecentCfdis[activeRfc][cfdiType]
          ? rfcRecentCfdis[activeRfc][cfdiType]
          : null
        : null,
  );

  const userIsInFTUE =
    (activeTab === "cfdiReceived" &&
      !!!active_rfc_obj.date_last_update_received_unix) ||
    (activeTab === "cfdiEmitted" &&
      !!!active_rfc_obj.date_last_update_emitted_unix);

  const [isLoadingCfdis, setLoadingCfdis] = useState(true);
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [paginationToken, setPaginationToken] = useState(true);
  const [canScrollToTop, setCanScrollToTop] = useState(null);
  const [countOnboardingAutoRefresh, setCountOnboardingAutoRefresh] =
    useState(0);

  const getUserCfdis = async (isManualRefresh) => {
    setLoadingCfdis(true);

    let isFirstPage = true;
    let payload = {};

    if (efosRfc) {
      if (cfdiType === "cfdiEmitted") {
        payload.rfc_type = "receiver";
        payload.rfc_emitter = activeRfc;
        payload.rfc_receiver = efosRfc;
      } else if (cfdiType === "cfdiReceived") {
        payload.rfc_type = "emitter";
        payload.rfc_emitter = efosRfc;
        payload.rfc_receiver = activeRfc;
      } else {
        throw new Error("Invalid cfdi type.");
      }
    } else {
      if (cfdiType === "cfdiEmitted") {
        payload.rfc_emitter = activeRfc;
      } else if (cfdiType === "cfdiReceived") {
        payload.rfc_receiver = activeRfc;
      } else {
        throw new Error("Invalid cfdi type.");
      }

      if (!!cfdiSearchFilters) {
        payload.search_filters = {};

        if (cfdiSearchFilters.hasOwnProperty("uuid")) {
          payload.id_cfdi = cfdiSearchFilters.uuid;
          payload.search_filters.uuid = cfdiSearchFilters.uuid;
        }

        if (
          cfdiSearchFilters.hasOwnProperty("date_from") &&
          cfdiSearchFilters.date_from &&
          cfdiSearchFilters.hasOwnProperty("date_to") &&
          cfdiSearchFilters.date_to
        ) {
          payload.search_filters.date_from = cfdiSearchFilters.date_from;
          payload.search_filters.date_to = cfdiSearchFilters.date_to;
        }

        if (
          cfdiSearchFilters.hasOwnProperty("name") &&
          cfdiSearchFilters.name
        ) {
          payload.search_filters.name = cfdiSearchFilters.name;
        }

        if (cfdiSearchFilters.hasOwnProperty("rfc") && cfdiSearchFilters.rfc) {
          payload.search_filters.rfc = cfdiSearchFilters.rfc;
        }

        if (
          cfdiSearchFilters.hasOwnProperty("amount_min") &&
          cfdiSearchFilters.amount_min
        ) {
          payload.search_filters.amount_min = cfdiSearchFilters.amount_min;
        }

        if (
          cfdiSearchFilters.hasOwnProperty("amount_max") &&
          cfdiSearchFilters.amount_max
        ) {
          payload.search_filters.amount_max = cfdiSearchFilters.amount_max;
        }

        if (
          cfdiSearchFilters.hasOwnProperty("tags") &&
          cfdiSearchFilters.tags
        ) {
          payload.search_filters.tags = cfdiSearchFilters.tags;
        }

        payload.search_filters = JSON.stringify(payload.search_filters);
      }
    }

    if (!isManualRefresh && paginationToken) {
      payload.pagination_token = paginationToken;
      isFirstPage = false;
    }

    let apiRes = null;
    if (efosRfc) {
      apiRes = await ApiManager.getUserCfdisRfcLink(
        payload.rfc_type,
        payload.rfc_emitter,
        payload.rfc_receiver,
        payload.pagination_token,
      );
    } else {
      apiRes = await ApiManager.getUserCfdis(
        payload.id_cfdi,
        payload.rfc_emitter,
        payload.rfc_receiver,
        payload.search_filters,
        payload.pagination_token,
      );
    }

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      setLoadingCfdis(false);
      return;
    }

    if (apiRes.response.code === 2000) {
      const { cfdis, pagination_token } = apiRes.data;

      setTableData((prev) => {
        return !isManualRefresh && prev ? prev.concat(cfdis) : cfdis;
      });

      setIsLazyLoading(false);

      if (!!!efosRfc && !!!cfdiSearchFilters && isFirstPage) {
        setRfcRecentCfdis(activeRfc, cfdiType, cfdis);
      }

      if (pagination_token) {
        setPaginationToken(pagination_token);
      } else {
        setPaginationToken(null);
      }
    } else if (apiRes.response.code === 1003) {
      setTableData([]);
      setIsLazyLoading(false);
      setPaginationToken(null);
    } else {
      message.error(strings.alertUnexpectedError);
    }

    setLoadingCfdis(false);
  };

  useEffect(() => {
    if (cfdiType !== activeTab) {
      return;
    }
    getUserCfdis(true);
  }, [activeRfc, cfdiSearchFilters]);

  useEffect(() => {
    const onScroll = debounce(() => {
      if (cfdiType !== activeTab) {
        return;
      }

      const percentScrolled =
        (document.scrollingElement.scrollTop /
          (document.scrollingElement.scrollHeight -
            document.scrollingElement.clientHeight)) *
        100;

      if (percentScrolled > 0) {
        setCanScrollToTop(true);
      } else {
        setCanScrollToTop(false);
      }

      if (
        !isLazyLoading &&
        paginationToken &&
        tableData &&
        !!tableData?.length &&
        percentScrolled >= 80
      ) {
        setIsLazyLoading(true);
        getUserCfdis(false);
      }
    }, 500);

    document.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("scroll", onScroll);
    };
  }, [paginationToken, tableData?.length]);

  const maxAutoRefreshAttempts = 30; // 5 minutes of auto refresh

  useEffect(() => {
    if (cfdiType !== activeTab) return;
    if (!userIsInFTUE || tableData?.length) return;

    if (countOnboardingAutoRefresh >= maxAutoRefreshAttempts) {
      return;
    }

    const intervalId = setInterval(() => {
      setCountOnboardingAutoRefresh((prev) => {
        const next = prev + 1;
        if (next >= maxAutoRefreshAttempts) {
          clearInterval(intervalId);
        }

        return next;
      });

      getUserCfdis(true);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [
    cfdiType,
    activeTab,
    userIsInFTUE,
    tableData,
    countOnboardingAutoRefresh,
  ]);

  const renderUnreadIcon = (item) =>
    item.is_unseen ? (
      <Tooltip title="Nuevo desde tu última sesión.">
        <span
          style={{
            cursor: "help",
            fontSize: "40px",
            color: "#1C5676",
            marginLeft: "-15px",
            marginRight: "-25px",
          }}
        >
          •
        </span>
      </Tooltip>
    ) : null;

  const renderButtons = (item) => {
    return (
      <Flex justify="flex-start" gap={5}>
        {active_rfc_obj.plan_name === "free" ? (
          <ModalUnsupportedPlan
            buttonTitle="Detalles"
            buttonIcon={<OrderedListOutlined />}
            descriptionTitlePrefix="Para ver los detalles de tus CFDIs"
            requiredPlans={["pro", "basic"]}
          />
        ) : (
          <Button
            type="primary"
            icon={<OrderedListOutlined />}
            size="small"
            onClick={() => navigate(`/cfdis/${item.id_cfdi}`)}
          >
            Detalles
          </Button>
        )}

        {["pro", "basic"].includes(active_rfc_obj.plan_name) ? (
          <>
            <CfdiFileButton type={"pdf"} file_url={item.pdf_url} />
            <CfdiFileButton type={"xml"} file_url={item.xml_url} />
          </>
        ) : (
          <>
            <ModalUnsupportedPlan
              buttonTitle="PDF"
              buttonIcon={<FilePdfOutlined />}
              descriptionTitlePrefix="Para ver el PDF de tus CFDIs"
              requiredPlans={["pro", "basic"]}
            />
            <ModalUnsupportedPlan
              buttonTitle="XML"
              buttonIcon={<FileTextOutlined />}
              descriptionTitlePrefix="Para ver el XML de tus CFDIs"
              requiredPlans={["pro", "basic"]}
            />
          </>
        )}
      </Flex>
    );
  };

  const renderInfo = (item) => (
    <Space direction="vertical" size={0}>
      <Text strong>
        {cfdiType === "cfdiReceived" ? item.emitter.name : item.receiver.name}
        &nbsp;&nbsp;
        {item.is_efos_linked ? <EfosTag /> : null}
      </Text>
      <Text strong style={{ color: "#801794" }}>
        {cfdiType === "cfdiReceived" ? item.emitter.rfc : item.receiver.rfc}
      </Text>
      <Text>{getFormattedUnixDate(item.date_created_at)}</Text>
      <Space size={0}>
        {item.user_data?.tags?.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </Space>
    </Space>
  );

  const handleScrollToTop = () => {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
  };

  const columns = isSmallView
    ? [
        {
          title: "",
          dataIndex: "unread",
          key: "unread",
          align: "center",
          width: 1,
          minWidth: 1,
          render: (_, { item }) => renderUnreadIcon(item),
        },
        {
          title: "Info",
          dataIndex: "info",
          key: "info",
          align: "left",
          render: (_, { item }) => {
            return (
              <Space direction="vertical" size={10}>
                {renderInfo(item)}
                <CfdiAmount
                  item={item}
                  amountType={"total"}
                  includeCurrency={true}
                  align={"left"}
                />
                <CfdiTags item={item} />
                {renderButtons(item)}
              </Space>
            );
          },
        },
      ]
    : isMediumView
      ? [
          {
            title: "",
            dataIndex: "unread",
            key: "unread",
            align: "center",
            width: 1,
            minWidth: 1,
            render: (_, { item }) => renderUnreadIcon(item),
          },
          {
            title: "Info",
            dataIndex: "info",
            key: "info",
            align: "left",
            render: (_, { item }) => {
              return (
                <Space direction="vertical" size={10}>
                  {renderInfo(item)}
                  <CfdiTags item={item} />
                </Space>
              );
            },
          },
          {
            title: "Info",
            dataIndex: "info",
            key: "info",
            align: "right",
            render: (_, { item }) => {
              return (
                <Space direction="vertical" size={10}>
                  <CfdiAmount
                    item={item}
                    amountType={"total"}
                    includeCurrency={true}
                    align={"right"}
                  />
                  {renderButtons(item)}
                </Space>
              );
            },
          },
        ]
      : [
          {
            title: "",
            dataIndex: "unread",
            key: "unread",
            align: "center",
            width: 1,
            minWidth: 1,
            render: (_, { item }) => renderUnreadIcon(item),
          },
          {
            title: "Info",
            dataIndex: "info",
            key: "info",
            align: "left",
            render: (_, { item }) => renderInfo(item),
          },
          {
            title: "Tags",
            key: "tags",
            dataIndex: "tags",
            align: "center",
            width: 1,
            minWidth: 1,
            render: (_, { item }) => <CfdiTags item={item} />,
          },
          {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            align: "center",
            width: 1,
            minWidth: 1,
            render: (_, { item }) => <CfdiAmount item={item} />,
          },
          {
            title: "",
            keys: "buttons",
            align: "right",
            width: 1,
            minWidth: 1,
            render: (_, { item }) => renderButtons(item),
          },
        ];

  let dataSource = tableData?.map((item, i) => {
    // item.is_unseen = false;
    // i > 3 ? (item.is_unseen = false) : (item.is_unseen = true);
    return {
      key: item.id_cfdi,
      item: item,
    };
  });

  const backfillYearsLabel =
    active_rfc_obj.plan_name === "pro"
      ? "de los últimos cinco años"
      : active_rfc_obj.plan_name === "basic"
        ? "de los últimos dos años"
        : "del último año";

  const backfillCTA =
    active_rfc_obj.plan_name === "pro" ? (
      <>
        <Text style={{ fontSize: "15px" }}>
          Es posible que existan CFDIs anteriores, sin embargo solo se muestran
          hasta cinco años de historial.
        </Text>
      </>
    ) : active_rfc_obj.plan_name === "basic" ? (
      <>
        <Text style={{ fontSize: "15px" }}>
          Si necesitas visualizar CFDIs anteriores, cámbiate al plan{" "}
          <>
            <CrownFilled className="fg-gold" /> Pro
          </>
          .
        </Text>
        <Button onClick={() => navigate("/plan")}>Ver Planes</Button>
      </>
    ) : (
      <>
        <Text style={{ fontSize: "15px" }}>
          Si necesitas visualizar CFDIs anteriores, escoge uno de los planes.
        </Text>
        <Button onClick={() => navigate("/plan")}>Ver Planes</Button>
      </>
    );

  const WelcomeMessage = () => (
    <Flex justify="center">
      <Space direction="vertical" align="center" style={{ marginTop: "50px" }}>
        <Text
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          ¡Hola, {active_rfc_obj.sat_fiscal_name}!
        </Text>

        <Text style={{ fontSize: "18px", textAlign: "center" }}>
          Tus CFDIs se están cargando. Te notificaremos cuando estén listos.
        </Text>

        {/* <Text
          style={{
            fontSize: "14px",
            color: "#565656",
          }}
        >
          Puedes cerrar esta ventana.
        </Text> */}

        <Loading3QuartersOutlined
          spin={true}
          style={{
            fontSize: "20px",
            color: "#801794",
            margin: "20px 0px",
          }}
        />
      </Space>
    </Flex>
  );

  return (
    <>
      {/* <Button
        type="primary"
        shape="circle"
        disabled={isLoadingCfdis}
        icon={<RedoOutlined spin={isLoadingCfdis} />}
        onClick={() => getUserCfdis(true)}
        style={{ float: "right", marginTop: isSmallView ? "-55px" : "-65px" }}
        size={isSmallView ? "small" : "middle"}
      /> */}

      {isLoadingCfdis && (
        <RedoOutlined
          spin={isLoadingCfdis}
          style={{
            fontSize: isSmallView ? "15px" : "15px",
            float: "right",
            color: theme_token.colorGray,
            marginTop: isSmallView ? "-50px" : "-50px",
          }}
        />
      )}

      <Button
        type="primary"
        shape="circle"
        icon={<ArrowUpOutlined />}
        onClick={handleScrollToTop}
        style={{
          zIndex: 100,
          backgroundColor: "#801794",
          position: "fixed",
          bottom: 20,
          right: 20,
          opacity: canScrollToTop ? 1 : 0,
          transition: "all .2s",
          visibility: canScrollToTop ? "visible" : "hidden",
        }}
      />

      {!!tableData?.length && (
        <Table
          size="small"
          columns={columns}
          // rowClassName={(record) => (record.item.is_unseen ? "row-unseen" : "")}
          dataSource={dataSource}
          locale={{ emptyText: null }}
          showHeader={false}
          pagination={false}
          isLoadingCfdis={isLoadingCfdis}
        />
      )}

      {(paginationToken || (isLoadingCfdis && !tableData?.length)) &&
        ((activeTab === "cfdiReceived" &&
          !!active_rfc_obj.date_last_update_received_unix) ||
          (activeTab === "cfdiEmitted" &&
            !!active_rfc_obj.date_last_update_emitted_unix)) && (
          <Flex justify="center">
            <Loading3QuartersOutlined
              spin
              style={{
                fontSize: "20px",
                color: "#801794",
                margin: "20px 0px",
              }}
            />
          </Flex>
        )}

      {!!!userIsInFTUE &&
        !!!paginationToken &&
        !!!isLoadingCfdis &&
        !!!cfdiSearchFilters && (
          <Card style={{ marginTop: "30px", marginBottom: "50px" }}>
            <Flex justify="center">
              <Space direction="vertical" style={{ textAlign: "center" }}>
                <Space>
                  <ExclamationCircleOutlined
                    style={{
                      fontSize: "20px",
                      color: "#1C5676",
                      marginTop: "4px",
                    }}
                  />
                  <Text style={{ fontSize: "15px" }}>
                    Se han cargado todos los CFDIs {backfillYearsLabel}.
                  </Text>
                </Space>
                {backfillCTA}
              </Space>
            </Flex>
          </Card>
        )}

      {!isLoadingCfdis && !!cfdiSearchFilters && !tableData?.length && (
        <Flex justify="center">
          <Space>
            <ExclamationCircleOutlined
              style={{
                fontSize: "20px",
                color: "#1C5676",
                margin: "50px 0px",
              }}
            />
            <Text style={{ fontSize: "15px", textAlign: "center" }}>
              No se encontraron resultados para tu búsqueda.
            </Text>
          </Space>
        </Flex>
      )}

      {userIsInFTUE && !tableData?.length && <WelcomeMessage />}
    </>
  );
}
